import router from '@/router'

/**
 * 知识库常见页面文案 → 路由 path
 * 优先使用本表；未命中再回退到 route.meta.title 匹配
 */
const AI_PAGE_ALIAS_MAP: Record<string, string> = {
  首页: '/index',
  发票管理: '/invoice-management',
  上传发票: '/invoice-management/upload',
  账单管理: '/bill-management',
  红字信息管理: '/red-info-management',
  发起红字申请: '/red-info-management/apply',
  账单明细: '/bill-management/list',
  申请付款: '/bill-management/apply-payment',
  付款申请: '/payment-management',
  付款申请管理: '/payment-management',
  下载列表: '/async-download',
  报价管理: '/enquiry-management',
  需求报价: '/enquiry-management/demand-sourcing',
  我的货源: '/enquiry-management/quotation-management',
  商品对码: '/enquiry-management/product-matching',
  供货管理: '/supply',
  供货记录: '/supply/record',
  摇钱树供货记录: '/supply/record-money-tree',
  YQS供货记录: '/supply/record-money-tree',
  入库异常反馈: '/supply/reject-record',
  药检报告上传: '/supply/cash-cow-drug-test-report',
  退货记录: '/supply/returns-record',
  申报管理: '/variety/management',
  品种申报: '/variety/declare',
  品种管理: '/create-supply/create',
  中药报价管理: '/variety/cm-quotation',
  消息中心: '/notice-center',
  帮助中心: '/help-center',
  实销实结自主结算: '/leyo-pay-center/',
  结算看板: '/leyo-pay-center#/index'
}

function buildTitlePathMap() {
  const map = new Map<string, string>()
  Object.entries(AI_PAGE_ALIAS_MAP).forEach(([title, path]) => {
    map.set(title, path)
  })
  router.getRoutes().forEach((route) => {
    const title = String(route.meta?.title || '').trim()
    if (!title || !route.path || route.path.includes(':')) return
    if (!map.has(title)) {
      map.set(title, route.path)
    }
  })
  return map
}

/** 从【财务协同 → 付款申请管理】这类文案解析出站内 path */
export function resolveAiPagePath(rawPathText: string): string | undefined {
  const text = rawPathText.replace(/&gt;/g, '>').trim()
  if (!text) return undefined

  // 直接是站内 path
  if (text.startsWith('/')) return text.split(/\s/)[0]

  const titleMap = buildTitlePathMap()
  if (titleMap.has(text)) return titleMap.get(text)

  const segments = text
    .split(/\s*(?:→|->|>|\/|／)\s*/)
    .map((item) => item.trim())
    .filter(Boolean)

  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const path = titleMap.get(segments[i])
    if (path) return path
  }
  return undefined
}

export function escapeAiHtml(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * 渲染 AI 消息：
 * 1. Markdown 外链 [文案](https://...)
 * 2. Markdown 站内链 [文案](/path)
 * 3. 知识库路径文案 【财务协同 → 付款申请管理】
 */
export function formatAiMessageHtml(content: string) {
  if (!content) return ''
  let html = escapeAiHtml(content)

  // 外链
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    (_match, title: string, url: string) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${title}</a>`
    }
  )

  // 站内 path markdown
  html = html.replace(/\[([^\]]+)\]\((\/[^)\s]+)\)/g, (_match, title: string, path: string) => {
    return `<a href="#" class="ai-route-link" data-ai-route="${path}">${title}</a>`
  })

  // 【菜单路径】
  html = html.replace(/【([^】]+)】/g, (match, inner: string) => {
    const path = resolveAiPagePath(inner)
    if (!path) return match
    return `<a href="#" class="ai-route-link" data-ai-route="${path}">${match}</a>`
  })

  return html
}
